import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Button, 
  Footer, 
  Page, 
  PageContent, 
  PageHeader, 
  Text, 
  TextInput, 
  ResponsiveContext 
} from 'grommet';
//import { Send, Robot, DocumentText, Image as ImageIcon } from '@hpe-design/icons-grommet';
import { Send, Robot, DocumentText } from '@hpe-design/icons-grommet';
import { getGlassStyle } from '../../helpers/GlassStyles';

export const AgentHome = () => {
  const size = useContext(ResponsiveContext);
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;

  const mainGlass = getGlassStyle(isDarkMode);
  const inputGlass = getGlassStyle(isDarkMode, 'input');

  // --- .NET MAUI Bridges ---
  useEffect(() => {
    // Hook for Voice Input
    window.updateChatInput = (newText) => {
      setMessage((prev) => {
        const spacer = prev.length > 0 ? ' ' : '';
        return prev + spacer + newText;
      });
    };

    // Hook for Camera/Gallery (from ProcessAndSendImageAsync)
    window.handleImageUpload = (dataUrl) => {
      setChatHistory((prev) => [
        ...prev, 
        { sender: 'user', type: 'image', content: dataUrl, timestamp: new Date() }
      ]);
    };

    // Hook for Documents (from PickDocumentAsync)
    window.handleFileUpload = (base64, fileName, contentType) => {
      setChatHistory((prev) => [
        ...prev, 
        { 
          sender: 'user', 
          type: 'file', 
          content: base64, 
          name: fileName, 
          mime: contentType,
          timestamp: new Date()
        }
      ]);
    };

    return () => {
      delete window.updateChatInput;
      delete window.handleImageUpload;
      delete window.handleFileUpload;
    };
  }, []);

  // --- Auto-response Logic for Sofia ---
  useEffect(() => {
    const lastMsg = chatHistory[chatHistory.length - 1];
    if (lastMsg?.sender === 'user') {
      let replyText = "I received your message!";
      
      if (lastMsg.type === 'image') replyText = "I see the image you shared. Let me analyze those visuals for you.";
      if (lastMsg.type === 'file') replyText = `I've received ${lastMsg.name}. I'll process the data in this report now.`;

      const timer = setTimeout(() => {
        setChatHistory(prev => [...prev, { 
          sender: 'sofia', 
          text: replyText 
        }]);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [chatHistory]);

  const handleSend = () => {
    if (message.trim()) {
      setChatHistory([...chatHistory, { sender: 'user', text: message, type: 'text' }]);
      setMessage('');
    }
  };

  return (
    <Page flex="grow" background="transparent">
      <Box margin={{ left: size === 'small' ? 'medium' : 'large' }}>
        <PageHeader 
          pad={{ top: 'none', bottom: 'xsmall' }} 
          title={<Text size="xlarge" weight="bold" color="text-strong">BI Console Agent</Text>} 
          subtitle={<Text color="text-weak" size="large">Your personal digital assistant</Text>}
        />
      </Box>

      <PageContent flex="grow" justify="start" align="center" pad={{ bottom: 'large' }}>

        <Box
          width={size === 'small' ? '92%' : 'large'} 
          overflow="hidden"
          margin={{
            horizontal: size === 'small' ? 'auto' : 'none',
            bottom: 'xxsmall',
            top: 'none',
          }}
          style={{ 
            ...mainGlass.container,
            transition: 'width 0.2s ease-in-out', 
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Text size="small" margin="xsmall">Note: this UI will be replaced and the target is heavily under construction. Placeholder for the moment.</Text>
        </Box>

        <Box
          width={size === 'small' ? '92%' : 'large'} 
          height={{ min: 'medium', max: '75vh' }}
          overflow="hidden"
          margin={size === 'small' ? { horizontal: 'auto' } : 'none'}
          style={{ 
            ...mainGlass.container,
            transition: 'width 0.2s ease-in-out', 
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
         
          {/* Header */}
          <Box direction="row" pad="small" background="brand" align="center" gap="small">
            <Robot size="large" color="white" />
            <Box direction="row" gap="xsmall">
                <Text size="large" color="white" weight="bold">Sofia</Text>
                <Text size="large" color="white" opacity="medium">— Beta</Text>
            </Box>
          </Box>

          {/* Messages Area */}
          <Box flex overflow={{ vertical: 'auto' }} pad="medium" gap="small">
            {chatHistory.length === 0 ? (
              <Box flex justify="center" align="center" opacity="medium">
                <Text size="large">How can I help you today?</Text>
              </Box>
            ) : (
              chatHistory.map((chat, index) => {
                const isUser = chat.sender === 'user';
                return (
                  <Box 
                    key={index}
                    alignSelf={isUser ? 'end' : 'start'}
                    width={{ max: '85%' }}
                    style={{
                      ...mainGlass.container,
                      backdropFilter: 'blur(12px) saturate(130%)',
                      WebkitBackdropFilter: 'blur(12px) saturate(130%)',
                      backgroundColor: isUser 
                        ? (isDarkMode ? 'rgba(1, 169, 130, 0.25)' : 'rgba(1, 169, 130, 0.15)') 
                        : mainGlass.container.backgroundColor,
                      padding: '12px',
                      borderRadius: isUser ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
                    }}
                  >
                    {/* Text Content */}
                    {chat.text && <Text size="medium" color={isUser && !isDarkMode ? 'brand' : 'text-strong'}>{chat.text}</Text>}

                    {/* Image Content */}
                    {chat.type === 'image' && (
                      <Box round="small" overflow="hidden" border={{ color: 'brand', opacity: 'low' }}>
                        <img src={chat.content} alt="Upload" style={{ maxWidth: '100%', display: 'block' }} />
                      </Box>
                    )}

                    {/* File Content */}
                    {chat.type === 'file' && (
                      <Box direction="row" align="center" gap="small" pad="small" background="rgba(0,0,0,0.1)" round="xsmall">
                        <DocumentText color="brand" />
                        <Box flex>
                          <Text size="small" weight="bold" truncate>{chat.name}</Text>
                          <Text size="xsmall" color="text-weak">{chat.mime}</Text>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>

          {/* Input Area */}
          <Box pad="medium" background="transparent" border={{ side: 'top', color: mainGlass.borderColor }}>
            <Box
              direction="row" align="center" gap="small"
              pad={{ horizontal: 'medium', vertical: 'xsmall' }}
              style={inputGlass.container}
            >
              <TextInput
                placeholder="Type or use voice..."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDownCapture={(e) => e.key === 'Enter' && handleSend()}
                focusIndicator={false}
                plain
              />
              <Button 
                icon={<Send size="small" />} 
                primary onClick={handleSend} 
                disabled={!message.trim()}
                style={{ borderRadius: '50%', padding: '8px' }}
              />
            </Box>
          </Box>
          
          <Footer pad={{ vertical: 'xsmall' }} justify="center">
            <Text size="xsmall" color="text-weak">AI-Generated. Powered by BI Lab.</Text>
          </Footer>
        </Box>
      </PageContent>
    </Page>
  );
};